<script setup lang="ts">
import { X } from 'lucide-vue-next'
import type { ListFilterCriterion } from '@/types/listFilters'

const props = defineProps<{
  criterion: ListFilterCriterion
}>()

const { t } = useI18n()

const MAX_VISIBLE_VALUES = 2

const valueLabel = computed(() => {
  const labels = props.criterion.selected.map(
    (value) => props.criterion.options.find((option) => option.value === value)?.label ?? value
  )
  const visible = labels.slice(0, MAX_VISIBLE_VALUES).join(', ')
  const hidden = labels.length - MAX_VISIBLE_VALUES
  return hidden > 0 ? `${visible} +${hidden}` : visible
})

const onOpenChange = (open: boolean) => {
  if (open) props.criterion.onOpen?.()
}
</script>

<template>
  <div
    class="inline-flex h-8 max-w-80 items-center rounded-full bg-primary/10 pr-1 text-sm text-primary"
    :data-testid="`list-filter-tag-${criterion.key}`"
  >
    <DropdownMenu @update:open="onOpenChange">
      <DropdownMenuTrigger
        class="flex h-full min-w-0 cursor-pointer items-center gap-1 rounded-l-full pr-1 pl-3 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <span class="shrink-0 text-primary/60">{{ t('listFilters.tagLabel', { label: criterion.label }) }}</span>
        <span class="truncate font-medium">{{ valueLabel }}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-72 p-0">
        <ListFilterOptions :criterion="criterion" />
      </DropdownMenuContent>
    </DropdownMenu>
    <button
      type="button"
      class="grid size-6 cursor-pointer place-content-center rounded-full text-primary/60 outline-none hover:bg-primary/10 hover:text-primary focus-visible:ring-[3px] focus-visible:ring-ring/50"
      :aria-label="t('listFilters.remove', { label: criterion.label })"
      :data-testid="`list-filter-tag-remove-${criterion.key}`"
      @click="criterion.clear()"
    >
      <X class="size-3.5" />
    </button>
  </div>
</template>
